import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  officeRoutingQuery,
  removeOfficeRoute,
  saveOfficeRoute,
  type OfficeRoutingPage as Page,
  type OfficeRoutingRule,
} from '../../api/officeRouting'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Office routing. Ecran nou, numai in v4.
 *
 * O regula pe sediu: tichetele oamenilor din sediul X pleaca la echipa Y si,
 * daca e ales, la omul Z. Se aplica tichetelor pe care nu le-a dat nimeni
 * nimanui - deschise fara echipa / om alese, sau venite pe email fara o adresa
 * anume. Fara nicio regula, totul merge ca pana acum.
 */

function failed(error: unknown) {
  toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
}

interface Draft {
  office_id: number
  group_id: number
  assignee: number
  enabled: boolean
}

const EMPTY: Draft = { office_id: 0, group_id: 0, assignee: 0, enabled: true }

export function OfficeRoutingPage() {
  const page = useQuery(officeRoutingQuery())
  const queryClient = useQueryClient()
  const reload = () => void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'office_routing'] })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <LoadFailed what="Office routing" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  return (
    <div className="od-office-routing">
      <SettingsHead
        strong="Office"
        rest="routing"
        subtitle={
          <>
            Send the tickets of the people of an office to a team, or to one person. It applies when nobody chose
            the team or the person: a ticket opened without them, or an email that was not sent to someone in
            particular. With no rule, tickets go where they went before.
          </>
        }
      />
      <Rules page={page.data} onSaved={reload} />
    </div>
  )
}

function Rules({ page, onSaved }: { page: Page; onSaved(): void }) {
  const [draft, setDraft] = useState<Draft | null>(null)

  const remove = useMutation({
    mutationFn: (id: number) => removeOfficeRoute(id),
    onSuccess: (answer) => {
      toast(answer.message, 'Office routing')
      onSaved()
    },
    onError: failed,
  })

  const edit = (rule: OfficeRoutingRule) =>
    setDraft({ office_id: rule.office_id, group_id: rule.group_id, assignee: rule.assignee, enabled: rule.enabled === 1 })

  return (
    <section className="od-office-routing-rules">
      {page.canSave && (
        <p>
          <button type="button" className="btn btn-success btn-xs od-route-add" onClick={() => setDraft({ ...EMPTY })}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add a rule
          </button>
        </p>
      )}

      {draft !== null && (
        <RuleForm
          page={page}
          draft={draft}
          onCancel={() => setDraft(null)}
          onSaved={() => {
            setDraft(null)
            onSaved()
          }}
        />
      )}

      {page.rules.length === 0 ? (
        <p className="color-light-grey od-route-empty">No rule yet. Tickets are not routed by office.</p>
      ) : (
        <table className="table od-route-table">
          <thead>
            <tr>
              <th>Office</th>
              <th>Team</th>
              <th>Person</th>
              <th>State</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {page.rules.map((rule) => (
              <tr key={rule.ID} className={rule.enabled === 1 ? '' : 'color-light-grey'}>
                <td>
                  <strong>{rule.office_name || 'office #' + rule.office_id}</strong>
                  {rule.company_name !== '' && <div className="color-light-grey">{rule.company_name}</div>}
                </td>
                <td>{rule.group_id > 0 ? rule.group_name || 'team #' + rule.group_id : '-'}</td>
                <td>{rule.assignee > 0 ? rule.assignee_name || 'user #' + rule.assignee : '-'}</td>
                <td>{rule.enabled === 1 ? 'On' : 'Off'}</td>
                <td className="aright" style={{ whiteSpace: 'nowrap' }}>
                  {page.canSave && (
                    <>
                      <button type="button" className="btn btn-default btn-xs od-route-edit" onClick={() => edit(rule)}>
                        <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn-default btn-xs od-route-remove"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(rule.ID)}
                      >
                        <i className="fas fa-times" aria-hidden="true"></i> Remove
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function RuleForm({
  page,
  draft,
  onCancel,
  onSaved,
}: {
  page: Page
  draft: Draft
  onCancel(): void
  onSaved(): void
}) {
  const [form, setForm] = useState<Draft>(draft)

  const save = useMutation({
    mutationFn: () => saveOfficeRoute(form),
    onSuccess: (answer) => {
      toast(answer.message, 'Office routing')
      onSaved()
    },
    onError: failed,
  })

  /* Omul pleaca impreuna cu echipa lui: cu o echipa aleasa, numai oamenii ei. */
  const people = form.group_id > 0 ? page.staff.filter((p) => p.group_id === form.group_id) : page.staff

  return (
    <form
      className="od-route-form well well-sm"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="od-route-office">Office</label>
          <select
            id="od-route-office"
            className="form-control"
            value={form.office_id === 0 ? '' : String(form.office_id)}
            onChange={(e) => setForm({ ...form, office_id: Number(e.target.value) || 0 })}
          >
            <option value="">Select...</option>
            {page.offices.map((o) => (
              <option key={o.ID} value={o.ID}>
                {o.company_name !== '' ? o.company_name + ' - ' : ''}
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 form-group">
          <label htmlFor="od-route-group">Team</label>
          <select
            id="od-route-group"
            className="form-control"
            value={form.group_id === 0 ? '' : String(form.group_id)}
            onChange={(e) => {
              const group = Number(e.target.value) || 0
              const keeps = page.staff.some((p) => p.ID === form.assignee && (group === 0 || p.group_id === group))
              setForm({ ...form, group_id: group, assignee: keeps ? form.assignee : 0 })
            }}
          >
            <option value="">No team</option>
            {page.groups.map((g) => (
              <option key={g.ID} value={g.ID}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 form-group">
          <label htmlFor="od-route-person">Person</label>
          <select
            id="od-route-person"
            className="form-control"
            value={form.assignee === 0 ? '' : String(form.assignee)}
            onChange={(e) => setForm({ ...form, assignee: Number(e.target.value) || 0 })}
          >
            <option value="">Nobody in particular</option>
            {people.map((p) => (
              <option key={p.ID} value={p.ID}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2 form-group">
          <label>&nbsp;</label>
          <div className="checkbox" style={{ marginTop: 0 }}>
            <label>
              <input
                type="checkbox"
                className="od-route-enabled"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />{' '}
              On
            </label>
          </div>
        </div>
      </div>
      <button type="submit" className="btn btn-primary btn-sm od-route-save" disabled={save.isPending}>
        Save
      </button>{' '}
      <button type="button" className="btn btn-default btn-sm" onClick={onCancel}>
        Cancel
      </button>
    </form>
  )
}
